import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useQuery, type ApolloError } from '@apollo/client';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { TIPS_PAGE_QUERY } from '../../graphql/queries/tipsPage.ts';
import { GET_MEETINGS_MINIMAL } from '../../graphql/queries/meetingsMinimal';
import { GET_DAILY_BEST_BETS } from '../../graphql/queries/dailyBestBets';
import { GET_TRACK_STATISTICS } from '../../graphql/queries/trackStatistics';
import { GET_RACE_FIELD } from '../../graphql/queries/raceField';
import { useBrand, type MascotMood } from '../brand';
import { useCopy } from '../copy';
import { BrandMascot } from '../BrandMascot';
import { useShell, useViewer, type PaywallProps } from '../shell';
import { useTrack } from '../track';
import { useTourSteps, type TourStep } from '../tour';
import { GoingBadge, Help, Seg } from '../primitives';
import { TracksDrawer, type DrawerMeeting } from '../chrome';
import { BestBetTile, type BestBet } from '../common/BestBetTile';
import { longDay, ordinal, pct, raceClock, roiDirection, roiText, shortDay, localTime, titleCase, spPlace, money, plural } from '../lib/format';
import { isoDate, parseDatePath, sameDay } from '../lib/dates';
import { PREF, useStickyPref } from '../lib/prefs';
import { useLiveRefresh } from '../lib/useLiveRefresh';
import { LiveUpcomingTips, useUpcoming } from '../home/useUpcoming';
import { nextRaceByMeeting } from '../home/lib';
import { RunnerCard } from './RunnerCard';
import { SpeedMap } from './SpeedMap';
import { FIELD_SORTS, FieldNews, RaceField } from './FieldView';
import { RaceStats } from '../race-stats/RaceStats';
import { useRaceResults } from './useRaceResults';
import {
  bestBetNumber,
  betClass,
  deriveBestBets,
  expectedGoing,
  findResult,
  finishOrder,
  isRun,
  isScratched,
  meetingDay,
  recordSpan,
  resultHits,
  winnerPriceLine,
  DAY_MOOD,
  DAY_MOOD_LABEL,
  MIN_DAY_WINNERS,
  type WinnerPrices,
  scorecard,
  stateSlug,
  type BetClass,
  type DayBet,
  type FieldRow,
  type FieldRunner,
  type FieldSort,
  type Race,
  type RaceResults,
  type Scratching,
  type Segment,
} from './lib';
import './tips.css';

interface TipsPageData {
  track: { track_id?: string; code: string; name: string; state: string; type?: string | null };
  meetingNote?: string | null;
  meetingNoteType?: string | null;
  trackConditions?: { weather?: string | null; trackCondition?: string | null; trackConditionNumber?: string | null; rail?: string | null } | null;
  predictedTrackConditions?: {
    predictedWeather?: string | null;
    predictedTrackCondition?: string | null;
    predictedTrackConditionNumber?: number | null;
    predictionConfidence?: string | null;
    predictionDate?: string | null;
  } | null;
  races: Race[];
  scratchings?: Scratching[] | null;
  winnerPrices?: WinnerPrices | null;
}

interface MeetingRow {
  meeting_id: string;
  track: { code: string; name: string; state: string; type?: string | null };
  raceResultSegments?: Segment[] | null;
}

type Track = (event: string, params?: Record<string, unknown>) => void;
type RecordMeeting = {
  meetingDate: string;
  meetingDateFormatted: string;
  meetingUrl: string;
  totalRaces: number;
  topPickWinners: number;
  allPicksWinners: number;
  trackCondition?: string | null;
  topPickROI: number | null;
  valuePickROI: number | null;
};

const MEDIAN_WINNER_HELP =
  `The middle winning starting price at this meeting so far: half the winners paid more, half less, so one big roughie can't skew it. ` +
  `"vs" is the same figure for this track over the last 12 months. ` +
  `Red: winners paying ${DAY_MOOD.vlong}× the usual or more. Amber: ${DAY_MOOD.long}× or more. ` +
  `Blue: ${DAY_MOOD.short}× or less, favourites saluting. Coloured once ${MIN_DAY_WINNERS} races have a winner.`;

const HomeIcon = () => (
  // Heroicons HomeIcon (outline), inlined so shared/ needs no icon package
  <svg className="hi" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="m2.25 12 8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25"
    />
  </svg>
);

const errorCode = (e: ApolloError | undefined): string | undefined =>
  e?.graphQLErrors?.map((g) => g.extensions?.code as string | undefined).find(Boolean);

const isAuthError = (e: ApolloError | undefined) =>
  !!e && (e.message?.includes('Authentication required') || e.graphQLErrors?.some((g) => g.message?.includes('Authentication required')) || errorCode(e) === 'UNAUTHENTICATED');

type RowsView = { kind: 'field' | 'compact'; runners: FieldRunner[]; loading: boolean; failed: boolean; sort: FieldSort };
/** The Compact view: the Field view's rows with no other runners, so only our tips, in pick order. */
const COMPACT: RowsView = { kind: 'compact', runners: [], loading: false, failed: false, sort: 'pick' };

const tourSteps = (markName: string): TourStep[] => [
  { target: 'meeting', title: 'The meeting', body: 'The track and how our tips have gone here today.' },
  { target: 'conditions', title: 'Conditions', body: 'Track rating, weather and rail. The arrow shows our forecast.' },
  { target: 'racenav', title: 'Race navigation', body: 'Jump straight to any race. Ticked races have been run; the house goes back to the home page.' },
  { target: 'record', title: 'Our record here', body: 'How our tips have gone at this track, over the last four meetings or all of them.' },
  {
    target: 'tipsview',
    title: 'Tips view',
    body: 'Cards shows our tips in full; Compact shows just our tips as rows, in pick order; Field lists every runner, our tips in their bet colour. We remember your choice.',
  },
  { target: 'race', title: 'A race', body: 'Race details. Speed map shows where each runner should settle; Results opens the finishing order once it has run.' },
  // Compact and Field only: Cards has no rows, so the tour skips this step there (and 'tip' here)
  {
    target: 'field',
    title: 'A runner',
    body:
      'Number, name and barrier in brackets. The dots after our top pick are our confidence indicator: the more that are filled, the more confident we are. ' +
      'Under the name: our tip and, once run, the result, then form and jockey. ' +
      'On the right: the price, with an arrow and the opening price struck through when it has moved, then our chance. Tap a row for more.',
  },
  {
    target: 'tip',
    title: 'A tip',
    body: `The colour is the bet: green win, gold place, blue each-way. ${markName} marks our strongest selections. Tap i for a guide to the card and + for the full form.`,
  },
  { target: 'drawer', title: 'Tracks', body: 'Jump to any other meeting today.' },
];

/**
 * The tips page for one meeting, with results folded in: route /:state/:track/:date
 * (date YYYYMMDD). Past dates are public; today and later need a subscription,
 * which the server enforces (SUBSCRIPTION_ACCESS_DENIED shows the locked panel).
 */
export function TipsView() {
  const { state, track, date } = useParams<{ state: string; track: string; date: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const viewer = useViewer();
  const copy = useCopy();
  const trackEvent = useTrack();
  const day = parseDatePath(date);
  const iso = day ? isoDate(day) : null;

  const tips = useQuery<{ tipsPage: TipsPageData | null }>(TIPS_PAGE_QUERY, {
    variables: { state: state?.toLowerCase(), trackName: track?.toLowerCase(), date: iso, analysisTypeId: viewer.analysisTypeId },
    skip: !state || !track || !iso,
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    errorPolicy: 'none',
  });
  const tp = tips.data?.tipsPage ?? null;
  const results = useRaceResults(date, tp?.track.code);
  const meetings = useQuery<{ meetings: MeetingRow[] }>(GET_MEETINGS_MINIMAL, {
    variables: { date: iso, hasTips: true, analysisTypeId: viewer.analysisTypeId },
    skip: !iso,
    errorPolicy: 'ignore',
  });
  const daily = useQuery<{ dailyBestBets: { bestBets: DayBet[] } | null }>(GET_DAILY_BEST_BETS, {
    variables: { date: iso, analysisTypeId: viewer.analysisTypeId },
    skip: !iso || !tp,
    errorPolicy: 'ignore',
  });

  const authError = isAuthError(tips.error);
  useEffect(() => {
    if (authError) navigate('/login', { state: { from: location.pathname + location.search }, replace: true });
  }, [authError, navigate, location.pathname, location.search]);

  // one page view per meeting and model, once the tips are in
  const tracked = useRef('');
  const raceResults = results.data?.getEnhancedRaceResults;
  useEffect(() => {
    const key = `${track}|${date}|${viewer.analysisTypeId}`;
    if (!tp || tips.loading || tracked.current === key) return;
    tracked.current = key;
    trackEvent('page_view', {
      pageType: 'tips',
      trackName: track,
      selectedDate: iso,
      analysisType: viewer.analysisTypeId,
      raceCount: tp.races?.length ?? 0,
      hasResults: (raceResults?.length ?? 0) > 0,
      trackType: tp.track.type,
      state: tp.track.state,
    });
  }, [tp, tips.loading, track, date, iso, viewer.analysisTypeId, raceResults, trackEvent]);

  // race day: results, scratchings, odds and the scorecard stay current until every race has run
  const liveDay = !!iso && sameDay(new Date(`${iso}T12:00:00`), new Date());
  const allRun = !!tp && tp.races.every((r) => isRun(raceResults?.find((x) => x.race_number === r.race_number)));
  useLiveRefresh(liveDay && !!tp && !allRun, [results.refetch, tips.refetch, meetings.refetch]);

  if (tips.loading && !tp) {
    return (
      <div className="ui-tips loading" aria-busy="true" aria-label="Loading tips">
        <div className="ui-sk" style={{ height: 40 }} />
        <div className="ui-sk" style={{ height: 100 }} />
        <div className="ui-sk" style={{ height: 200 }} />
      </div>
    );
  }

  const home = `/${date ?? ''}`;
  if (authError) return <div className="ui-tips loading" aria-busy="true" />;
  if (tips.error) {
    const denied = tips.error.graphQLErrors?.find((g) => g.extensions?.code === 'SUBSCRIPTION_ACCESS_DENIED');
    if (denied) {
      const name = (denied.extensions?.trackName as string) || titleCase(track?.replace(/-/g, ' ')) || 'this track';
      return <Locked trackName={name} state={state} track={track} date={date} />;
    }
    return (
      <Message title="We couldn't load these tips" home={home} mood="confused">
        {tips.error.message.includes('start_time') ? "We're updating our tips data. Please check back later." : 'Please try again in a moment.'}
      </Message>
    );
  }
  if (!tp) {
    return (
      <Message title="Tips not found" home={home} mood="confused">
        We have no tips for {titleCase(track?.replace(/-/g, ' '))} on {iso ? longDay(iso) : date}.
      </Message>
    );
  }
  const hasTips = tp.races?.some((r) => r.selections?.top?.length || r.selections?.value?.length);
  if (!hasTips) {
    return (
      <Message title={`No tips for ${titleCase(tp.track.name)} yet`} home={home} mood="bored">
        {copy.noTipsYet}
      </Message>
    );
  }

  return (
    <Meeting
      tp={tp}
      date={date!}
      iso={iso!}
      results={raceResults ?? []}
      meetings={meetings.data?.meetings ?? []}
      dayBets={daily.data?.dailyBestBets?.bestBets ?? []}
      trackEvent={trackEvent}
    />
  );
}

function Message({ title, home, mood, children }: { title: string; home: string; mood: MascotMood; children: ReactNode }) {
  const copy = useCopy();
  return (
    <div className="ui-tips">
      <section className="ui-tmsg">
        <BrandMascot mood={mood} />
        <h1>{title}</h1>
        <p>{children}</p>
        <Link to={home} className="ui-tbtn">
          {copy.backToMeetings}
        </Link>
      </section>
    </div>
  );
}

/** Today's and future tips are for subscribers: say so and point at the plans. */
function Locked({ trackName, state, track, date }: PaywallProps) {
  const { Paywall } = useShell();
  const trackEvent = useTrack();
  useEffect(() => {
    trackEvent('paywall_view', { trackName: track });
  }, [trackEvent, track]);
  if (Paywall) return <Paywall trackName={trackName} state={state} track={track} date={date} />;
  const remember = () => {
    try {
      // PaymentSuccess sends the viewer back to this meeting
      sessionStorage.setItem('payment_return_track', JSON.stringify({ state, track, date }));
    } catch {
      // ponytail: storage unavailable - they land on the home page after paying instead
    }
  };
  return (
    <div className="ui-tips">
      <section className="ui-tmsg locked" aria-label="Subscribers only">
        <span className="lk" aria-hidden="true">
          🔒
        </span>
        <h1>{trackName} tips are for subscribers</h1>
        <p>
          Subscribe for our analysis of every race at {trackName}: selections, our price and chance, speed maps and the full form. Past
          meetings stay free to look back on.
        </p>
        <div className="acts">
          <Link
            to="/pricing"
            className="ui-tbtn"
            onClick={() => {
              remember();
              trackEvent('ui_click', { target: 'see_plans', trackName: track });
            }}
          >
            See plans
          </Link>
          <Link to="/redeem" className="ui-tbtn quiet">
            Have a promo code?
          </Link>
        </div>
      </section>
    </div>
  );
}

function Meeting({
  tp,
  date,
  iso,
  results,
  meetings,
  dayBets,
  trackEvent,
}: {
  tp: TipsPageData;
  date: string;
  iso: string;
  results: RaceResults[];
  meetings: MeetingRow[];
  dayBets: DayBet[];
  trackEvent: Track;
}) {
  const nextRace = nextRaceByMeeting(useUpcoming().rows);
  const brand = useBrand();
  const { analysisTypeId, trackStats } = useViewer();
  const location = useLocation();
  const [view, setView] = useStickyPref(PREF.tipsView, 'cards', ['cards', 'compact', 'field'] as const);
  // the Field view's announcement, until the viewer clears it or picks Field
  const [fieldNews, setFieldNews] = useStickyPref(PREF.fieldNews, 'shown', ['shown', 'dismissed'] as const);
  const [fieldSort, setFieldSort] = useStickyPref<FieldSort>(PREF.fieldSort, 'number', ['number', 'odds', 'barrier', 'settle']);
  // races flipped between tips and their field from the race heading; a new tips view clears them
  const [flipped, setFlipped] = useState<Map<string, boolean>>(new Map());
  const [period, setPeriod] = useState<'recent' | 'all'>('recent');
  const [forecast, setForecast] = useState(false);
  const [recordOpen, setRecordOpen] = useState(false);
  const scroller = useRef<HTMLDivElement>(null);
  useTourSteps(tourSteps(brand.markName));

  const code = tp.track.code;
  const trackName = titleCase(tp.track.name);
  const stateLabel = stateSlug(tp.track.state).toUpperCase();
  const typeLabel = titleCase(tp.track.type);
  const today = sameDay(new Date(`${iso}T12:00:00`), new Date());
  const resultOf = (n: number) => results.find((r) => r.race_number === n);
  const run = new Set(tp.races.filter((r) => isRun(resultOf(r.race_number))).map((r) => r.race_number));
  const next = today ? tp.races.find((r) => !run.has(r.race_number))?.race_number : undefined;
  const sc = scorecard(results);
  const segments = meetings.find((m) => m.track.code === code)?.raceResultSegments ?? [];
  const selectedDate = iso;
  const on = (contentType: string, open: boolean, raceNumber?: number, extra?: Record<string, unknown>) =>
    trackEvent(open ? 'content_expand' : 'content_collapse', {
      pageType: 'tips',
      trackName: code,
      raceNumber,
      selectedDate,
      contentType,
      analysisType: analysisTypeId,
      ...extra,
    });
  const toggled = (control: string, value: string, extra?: Record<string, unknown>) =>
    trackEvent('ui_toggle', { trackName: code, selectedDate, control, value, ...extra });
  const clicked = (target: string, extra?: Record<string, unknown>) =>
    trackEvent('ui_click', { trackName: code, selectedDate, target, ...extra });
  const pickView = (v: typeof view, source = 'switch') => {
    setView(v);
    setFlipped(new Map());
    if (v === 'field') setFieldNews('dismissed');
    toggled('tips_view', v, { source });
  };
  // Which view each meeting is read in. The choice is remembered, so the switch's own event only
  // counts changes; this counts use, including everyone who stays on a view they picked before.
  useEffect(() => {
    trackEvent('tips_view_shown', { pageType: 'tips', trackName: code, selectedDate, view });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once per meeting and view, not per render
  }, [code, selectedDate, view]);

  const record = useQuery(GET_TRACK_STATISTICS, {
    variables: { trackCode: code, analysisTypeId, meetingLimit: period === 'recent' ? 4 : null },
    skip: !code,
    errorPolicy: 'ignore',
  });
  const stats = record.data?.trackStatistics;

  const showsField = (raceId: string) => flipped.get(raceId) ?? view === 'field';
  const anyField = tp.races.some((r) => showsField(r.race_id));
  // one query for the meeting, so flipping a second race (or the whole view) reads the cache
  const field = useQuery(GET_RACE_FIELD, {
    variables: { raceIds: tp.races.map((r) => r.race_id) },
    skip: !anyField,
  });
  const fieldByRace = useMemo(() => {
    const m = new Map<string, FieldRunner[]>();
    for (const r of (field.data?.raceField ?? []) as FieldRunner[]) m.set(r.raceId, [...(m.get(r.raceId) ?? []), r]);
    return m;
  }, [field.data]);
  // race day: the field's prices move with the market, like the tips' own
  useLiveRefresh(anyField && today && run.size < tp.races.length, [field.refetch]);
  const meetingsHere: RecordMeeting[] = stats?.recentMeetings ?? [];

  const bets = useMemo(() => {
    const d = deriveBestBets(tp.races, tp.scratchings ?? [], results);
    const tile = (b: (typeof d.best)[number], kind: BestBet['kind'], i: number): BestBet => {
      const race = tp.races.find((r) => r.race_number === b.race);
      return {
        kind,
        index: bestBetNumber(dayBets, code, b.race, b.number, i + 1),
        track: trackName,
        race: b.race,
        number: b.number,
        horse: b.name,
        price: b.marketOdds,
        time: race?.start_time ? localTime(race.start_time) : undefined,
        edge: b.edge,
        position: b.finishPosition ?? null,
        sp: b.sp,
        why: b.analysis,
        chance: b.adjustedAssessedProb,
        ourPrice: b.odds,
        overlay: kind === 'value' && b.odds ? (b.marketOdds / b.odds - 1) * 100 : null,
        href: `${location.pathname}?scrollToRace=${b.race}`,
      };
    };
    return [...d.best.map((b, i) => tile(b, 'best', i)), ...(d.value ? [tile(d.value, 'value', 0)] : [])];
  }, [tp, results, dayBets, code, trackName, location.pathname]);

  // ?scrollToRace=N (from the home page and best bets)
  useEffect(() => {
    const n = new URLSearchParams(location.search).get('scrollToRace');
    if (!n) return;
    const t = window.setTimeout(() => document.getElementById(`race-${n}`)?.scrollIntoView({ block: 'start' }), 300);
    return () => window.clearTimeout(t);
    // location.key: tapping the same best bet again is a new navigation to the same address
  }, [location.search, location.key]);

  // keep the next race's pill in view
  useEffect(() => {
    const box = scroller.current;
    const pill = box?.querySelector<HTMLElement>('.next');
    if (box && pill) box.scrollLeft = Math.max(0, pill.offsetLeft - box.offsetLeft - 60);
  }, [next]);

  const cond = tp.trackConditions ?? {};
  const num = Number(cond.trackConditionNumber);
  const going = cond.trackCondition ? `${cond.trackCondition}${num > 0 ? ` ${num}` : ''}` : null;
  const pred = tp.predictedTrackConditions;
  const expected = expectedGoing(cond.trackCondition, pred?.predictedTrackCondition, pred?.predictedTrackConditionNumber);
  const drawer: DrawerMeeting[] = meetings.map((m) => ({
    key: m.track.code,
    name: m.track.name,
    state: stateSlug(m.track.state),
    type: m.track.type,
    href: `/${stateSlug(m.track.state)}/${m.track.code}/${date}`,
    next: nextRace.get(m.meeting_id),
  }));
  const top = stats?.topPickPerformance;
  const value = stats?.valuePickPerformance;
  const all = stats?.allPicksPerformance;
  const wp = winnerPriceLine(tp.winnerPrices);
  const roiB = (r: number | null | undefined) =>
    r == null ? <b>–</b> : <b className={roiDirection(r)}>{roiText(r * 100)}</b>;

  return (
    <div className="ui-tips" id="tips-top" data-view={view}>
      <LiveUpcomingTips />
      <div className="ui-th" data-tour="meeting">
        <div>
          <h1>{trackName}</h1>
          <div className="meta">
            {longDay(iso)} · {stateLabel}
            {typeLabel && ` · ${typeLabel}`}
          </div>
        </div>
        {sc.run > 0 && (
          <div className="ui-score" aria-label="Meeting scorecard" data-n={wp?.today != null ? 4 : 3}>
            <div className="kpi">
              <div className="kl">Top pick won</div>
              <div className="kv">
                {sc.topWon}/{sc.run}
                <small>{pct((sc.topWon / sc.run) * 100, 0)}</small>
              </div>
            </div>
            <div className="kpi">
              <div className="kl">Any tip won</div>
              <div className="kv">
                {sc.anyWon}/{sc.run}
                <small>{pct((sc.anyWon / sc.run) * 100, 0)}</small>
              </div>
            </div>
            <div className="kpi">
              <div className="kl">Top pick ROI</div>
              <div className="kv">
                <b className={roiDirection(sc.roi)}>{roiText(sc.roi)}</b>
              </div>
            </div>
            {wp?.today != null && (
              <div className="kpi mw" data-mood={wp.mood ?? undefined}>
                <div className="kl">
                  Median winner
                  <Help title="Median winner" text={MEDIAN_WINNER_HELP} />
                </div>
                <div className="kv">
                  {money(wp.today)}
                  {wp.dir && (
                    <i className="wd" aria-label={wp.dir === 'up' ? 'longer than usual' : 'shorter than usual'}>
                      {wp.dir === 'up' ? ' ▲' : ' ▼'}
                    </i>
                  )}
                  {wp.typical != null && <small>vs {money(wp.typical)}</small>}
                </div>
                <div className="md">
                  {wp.mood
                    ? DAY_MOOD_LABEL[wp.mood]
                    : wp.typical == null
                      ? 'New track: no comparison'
                      : `${plural(wp.todayWinners, 'winner')} so far`}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="ui-cond" data-open={forecast} data-expected={!!expected} data-tour="conditions" role="group" aria-label="Conditions">
        <span className="ci">
          <span className="k">Track</span>
          <GoingBadge condition={going} />
        </span>
        {expected && (
          <span className="ci" title="The going our tips were generated for">
            <span className="k">Expected</span>
            <GoingBadge condition={expected} />
          </span>
        )}
        <span className="ci">
          <span className="k">Weather</span>
          <b>{cond.weather || '–'}</b>
        </span>
        <span className="ci">
          <span className="k">Rail</span>
          <b>{cond.rail || '–'}</b>
        </span>
        {pred?.predictedTrackCondition && (
          <>
            <button
              type="button"
              className="fc"
              aria-expanded={forecast}
              aria-label={forecast ? 'Hide the forecast' : 'Show the forecast'}
              title="Forecast"
              onClick={() => {
                setForecast(!forecast);
                on('track_forecast', !forecast);
              }}
            >
              <span className="chev" aria-hidden="true">
                ▾
              </span>
            </button>
            {forecast && (
              <div className="pred">
                We forecast {pred.predictedTrackCondition}
                {pred.predictedTrackConditionNumber ? ` ${pred.predictedTrackConditionNumber}` : ''}
                {pred.predictionConfidence && ` (${pred.predictionConfidence} confidence)`}
                {pred.predictionDate && ` on ${shortDay(pred.predictionDate.slice(0, 10))}`}.{pred.predictedWeather && ` ${pred.predictedWeather}.`}
                {expected && ` Our tips were generated expecting ${expected}.`}
              </div>
            )}
          </>
        )}
      </div>

      {tp.meetingNote && (
        <div className={`ui-note ${tp.meetingNoteType || 'info'}`} role="note">
          <span aria-hidden="true">ℹ</span>
          <span>{tp.meetingNote}</span>
        </div>
      )}

      <nav className="ui-pills" aria-label="Races" data-tour="racenav">
        <span className="ui-pills-fixed">
          <Link to={`/${date}`} aria-label="Home" onClick={() => clicked('race_nav_home')}>
            <HomeIcon />
          </Link>
          <a href="#tips-top" onClick={() => clicked('race_nav_top')}>
            Top
          </a>
        </span>
        <div className="scroller" ref={scroller}>
          {tp.races.map((r) => (
            <a
              key={r.race_id}
              href={`#race-${r.race_number}`}
              className={run.has(r.race_number) ? 'done' : r.race_number === next ? 'next' : ''}
              aria-label={`Race ${r.race_number}${run.has(r.race_number) ? ', run' : r.race_number === next ? ', next' : ''}`}
              onClick={() => clicked('race_nav', { raceNumber: r.race_number })}
            >
              R{r.race_number}
            </a>
          ))}
        </div>
      </nav>

      {/* hidden only once the server says there is no record, not while a period loads */}
      {record.data?.trackStatistics !== null && (
        <div className="ui-ts" data-open={recordOpen} data-tour="record">
          <div className="tsh">
            <h3>Our record at {trackName}</h3>
            {trackStats && (
              <Link className="stats" to={`/tracks/${code}`} onClick={() => clicked('track_stats')}>
                Track stats
              </Link>
            )}
            <Seg
              label="Period"
              tone="dark"
              value={period}
              onChange={(p) => {
                setPeriod(p);
                toggled('track_stats_range', p);
              }}
              options={[
                { value: 'recent', label: 'Recent' },
                { value: 'all', label: 'All' },
              ]}
            />
          </div>
          <button
            type="button"
            aria-expanded={recordOpen}
            onClick={() => {
              setRecordOpen(!recordOpen);
              on('track_statistics', !recordOpen, undefined, { hasTopPickData: !!top, hasValuePickData: !!value });
            }}
          >
            <div className="nums">
              <span>
                Top pick won<b>{pct(top?.winPercentage)}</b>
              </span>
              <span>Top pick ROI{roiB(top?.roi)}</span>
              <span>Value ROI{roiB(value?.roi)}</span>
              <span>
                Any tip won<b>{pct(all?.winPercentage)}</b>
              </span>
            </div>
            <span className="chev" aria-hidden="true">
              ▾
            </span>
          </button>
          {recordOpen && (
            <div className="body2">
              {recordSpan(meetingsHere.map((m) => m.meetingDate), period)}:{' '}
              {top?.winners ?? 0} top-pick winners from {top?.races ?? 0} races here · {value?.winners ?? 0} value winners ·{' '}
              {all?.winners ?? 0} races where any tip won.
              {meetingsHere.length > 0 && (
                <div className="mt">
                  <table>
                    <thead>
                      <tr>
                        <th>Meeting</th>
                        <th>Top won</th>
                        <th>Any won</th>
                        <th>Top ROI</th>
                        <th>Value ROI</th>
                      </tr>
                    </thead>
                    <tbody>
                      {meetingsHere.map((m) => (
                        <tr key={m.meetingDate}>
                          <td>
                            <GoingBadge condition={m.trackCondition} iconOnly />
                            <Link to={m.meetingUrl} onClick={() => clicked('track_stats_meeting', { meetingDate: m.meetingDateFormatted })}>
                              {meetingDay(m.meetingDate).toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: '2-digit' })}
                            </Link>
                          </td>
                          <td>
                            {m.topPickWinners}/{m.totalRaces}
                          </td>
                          <td>
                            {m.allPicksWinners}/{m.totalRaces}
                          </td>
                          <td>{roiB(m.topPickROI)}</td>
                          <td>{roiB(m.valuePickROI)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {bets.length > 0 && (
        <>
          <section className="ui-tsec">
            <div className="ui-tsh">
              <h2>Best bets here</h2>
            </div>
            <div className="ui-tgrid3">
              {bets.map((b) => (
                <BestBetTile key={`${b.kind}-${b.race}-${b.number}`} bet={b} />
              ))}
            </div>
          </section>
        </>
      )}

      {fieldNews === 'shown' && view !== 'field' && (
        <FieldNews
          onTry={() => {
            clicked('field_news_try');
            pickView('field', 'field_news');
          }}
          onDismiss={() => {
            clicked('field_news_dismiss');
            setFieldNews('dismissed');
          }}
        />
      )}
      <div className="ui-vtg" data-tour="tipsview">
        <span>Tips view</span>
        <Seg
          label="Tips view"
          value={view}
          onChange={(v) => pickView(v)}
          options={[
            { value: 'cards', label: 'Cards' },
            { value: 'compact', label: 'Compact' },
            { value: 'field', label: 'Field' },
          ]}
        />
      </div>
      {view === 'field' && (
        <div className="ui-vtg ui-fsort">
          <span>Order</span>
          <Seg
            label="Field order"
            value={fieldSort}
            onChange={(v) => {
              setFieldSort(v);
              toggled('field_sort', v);
            }}
            options={FIELD_SORTS}
          />
        </div>
      )}

      {tp.races.map((race) => (
        <RaceBlock
          key={race.race_id}
          race={race}
          result={resultOf(race.race_number)}
          segment={segments.find((s) => s.race_number === race.race_number)}
          ran={run.has(race.race_number)}
          isNext={race.race_number === next}
          scratchings={tp.scratchings ?? []}
          showField={showsField(race.race_id)}
          onFlipField={(show) => {
            setFlipped(new Map(flipped).set(race.race_id, show));
            toggled('race_field', show ? 'field' : 'tips', { raceNumber: race.race_number });
          }}
          field={
            showsField(race.race_id)
              ? { kind: 'field', runners: fieldByRace.get(race.race_id) ?? [], loading: field.loading, failed: !!field.error, sort: fieldSort }
              : view === 'compact'
                ? COMPACT
                : null
          }
          on={on}
        />
      ))}

      <div className="ui-totop-rail">
        <button
          type="button"
          className="ui-totop"
          aria-label="Back to top"
          title="Back to top"
          onClick={() => {
            window.scrollTo({ top: 0 });
            clicked('back_to_top');
          }}
        >
          ↑
        </button>
      </div>
      <TracksDrawer
        title={trackName}
        subtitle={`${stateLabel}${typeLabel ? ` · ${typeLabel}` : ''} · ${meetings.length} meeting${meetings.length === 1 ? '' : 's'}${today ? ' today' : ''}`}
        meetings={drawer}
        currentKey={code}
      />
    </div>
  );
}

/**
 * The analysis model switch, on the race header. Staff-only: the host app decides who
 * may switch by what it puts in `viewer.analysisTypes`, and nothing renders without a
 * real choice, so a subscriber never sees it. The AI model that wrote the race's tips
 * rides along under the same condition.
 */
function ModelSwitch({ aiModel }: { aiModel?: string | null }) {
  const { analysisTypeId, analysisTypes, setAnalysisType } = useViewer();
  const models = analysisTypes ?? [];
  if (models.length < 2 || !setAnalysisType) return null;
  return (
    <div className="mdl">
      {aiModel && <span className="aim" title="AI model that wrote these tips">{aiModel}</span>}
      <Seg
        label="Analysis model"
        value={String(analysisTypeId)}
        onChange={(v) => setAnalysisType(Number(v))}
        options={models.map((m) => ({ value: String(m.id), label: m.name, title: m.title }))}
      />
    </div>
  );
}

function RaceBlock({
  race,
  result,
  segment,
  ran,
  isNext,
  scratchings,
  field,
  showField,
  onFlipField,
  on,
}: {
  race: Race;
  result: RaceResults | undefined;
  segment: Segment | undefined;
  ran: boolean;
  isNext: boolean;
  scratchings: Scratching[];
  /** Set in the Field and Compact views: one line per runner (every runner, or just our tips) in place of the tip cards. */
  field: RowsView | null;
  /** Whether this race shows its whole field, flipped from its heading or by the Field view. */
  showField: boolean;
  onFlipField: (show: boolean) => void;
  on: (contentType: string, open: boolean, raceNumber?: number, extra?: Record<string, unknown>) => void;
}) {
  const copy = useCopy();
  const [showResult, setShowResult] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  // stays true once opened, so the stats are not dropped mid-slide as the panel closes
  const [opened, setOpened] = useState(false);
  const trackEvent = useTrack();
  const n = race.race_number;
  const top = race.selections?.top ?? [];
  const value = race.selections?.value ?? [];
  const map = race.selections?.speedMap;
  // the speed map and race stats share one panel, a tab each when the race has a speed map
  const [tab, setTab] = useState<'map' | 'stats'>('map');
  const shown = map && tab === 'map' ? 'map' : 'stats';
  const contentType = shown === 'map' ? 'speed_map' : 'race_stats';
  const picks = new Map<number, BetClass>();
  for (const s of [...top, ...value]) if (!picks.has(s.number)) picks.set(s.number, betClass(s.bet_type, s.bet_stake));
  const finishers = ran && result ? finishOrder(result) : [];
  const hits = ran && result ? resultHits(result, segment) : [];
  const detail = [race.distance ? `${race.distance}m` : '', race.class ?? ''].filter(Boolean);
  const group = race.group?.trim();

  const card = (sel: (typeof top)[number], kind: 'top' | 'value') => (
    <RunnerCard
      key={`${kind}-${sel.number}`}
      sel={sel}
      kind={kind}
      scratched={isScratched(scratchings, n, sel.number) || !!findResult(result, sel.number, sel.horse_name)?.scratched}
      pending={!ran}
      result={findResult(result, sel.number, sel.horse_name)}
      onToggle={(open) =>
        on('horse_analysis', open, n, {
          horseNumber: sel.number,
          horseName: sel.horse_name,
          selectionType: kind,
          betType: sel.bet_type,
          odds: sel.odds,
        })
      }
    />
  );

  return (
    <section className="ui-race" id={`race-${n}`} aria-labelledby={`race-${n}-h`}>
      <div className="ui-rh" data-open={showResult} data-sm={showPanel} data-tour="race">
        <span className="no" id={`race-${n}-h`}>
          Race {n}
        </span>
        <span className="nm">
          <b>{race.race_name}</b>
          {detail.length > 0 && ` · ${detail.join(' · ')}`}
          {/* Group 1-3 and Listed races carry the gold badge they always have */}
          {group && <span className="grp">{group}</span>}
        </span>
        <ModelSwitch aiModel={race.aiModel} />
        <span className="rhb">
          <button
            type="button"
            className="smb"
            aria-expanded={showPanel}
            onClick={() => {
              setShowPanel(!showPanel);
              setOpened(true);
              on(contentType, !showPanel, n);
            }}
          >
            Stats{' '}
            <span className="chev" aria-hidden="true">
              ▾
            </span>
          </button>
          <button type="button" className="smb" aria-pressed={showField} onClick={() => onFlipField(!showField)}>
            Field
          </button>
        </span>
        {ran ? (
          <button
            type="button"
            className="stat fin"
            aria-expanded={showResult}
            onClick={() => {
              setShowResult(!showResult);
              on('race_results', !showResult, n);
            }}
          >
            Results{' '}
            <span className="chev" aria-hidden="true">
              ▾
            </span>
          </button>
        ) : race.start_time ? (
          <RaceClock start={race.start_time} isNext={isNext} />
        ) : null}
        <div className="smw" inert={!showPanel}>
          <div>
            <div className="ui-rp">
              {map && (
                <Seg
                  label="Race panel"
                  value={shown}
                  onChange={(v) => {
                    setTab(v);
                    trackEvent('ui_toggle', { control: 'race_panel', value: v, raceNumber: n });
                  }}
                  options={[
                    { value: 'map', label: 'Speed map' },
                    { value: 'stats', label: 'Race stats' },
                  ]}
                />
              )}
              {shown === 'map' && map ? (
                <SpeedMap map={map} picks={picks} raceNumber={n} />
              ) : (
                // mounted only once opened, so each race's stats are fetched on demand
                opened && <RaceStats raceId={race.race_id} />
              )}
            </div>
          </div>
        </div>
        {ran && (
          <div className="rsw" inert={!showResult}>
            <div>
              <div className="ui-rs">
                <div className="t">
                  Result · {finishers.length} ran
                  <span className="hits">
                    {hits.length ? (
                      hits.map((h) => (
                        <span key={h} className="hit">
                          ✓ {h}
                        </span>
                      ))
                    ) : (
                      <span className="none">No tip won</span>
                    )}
                  </span>
                </div>
                <ol>
                  {finishers.map((f) => (
                    <li key={`${f.pos}-${f.horse}`}>
                      <span className="p">{ordinal(f.pos)}</span>
                      <span>{f.horse}</span>
                      {f.ours ? <span className="ours">{f.ours}</span> : <span />}
                      <span className="sp">{spPlace(f.sp, f.place)}</span>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </div>
        )}
      </div>
      {field ? (
        <RaceField
          race={race}
          runners={field.runners}
          loading={field.loading}
          failed={field.failed}
          scratchings={scratchings}
          result={result}
          ran={ran}
          sort={field.sort}
          name={field.kind === 'compact' ? 'tips' : 'field'}
          onToggle={(row: FieldRow, open: boolean) =>
            // a Compact row opens the same analysis a card does, so it reports exactly like one
            field.kind === 'compact' && row.pick
              ? on('horse_analysis', open, n, {
                  horseNumber: row.number,
                  horseName: row.horse,
                  selectionType: row.pick.kind,
                  betType: row.pick.sel.bet_type,
                  odds: row.pick.sel.odds,
                })
              : on('field_runner', open, n, {
                  horseNumber: row.number,
                  horseName: row.horse,
                  selectionType: row.pick?.kind ?? 'none',
                  odds: row.odds,
                })
          }
        />
      ) : (
        top.length > 0 && (
          <>
            <div className="ui-sub">{copy.topSelections}</div>
            <div className="ui-rcs" data-tour="tip">
              {top.map((s) => card(s, 'top'))}
            </div>
          </>
        )
      )}
      {!field && value.length > 0 && (
        <>
          <div className="ui-sub">{copy.valueSelection}</div>
          <div className="ui-rcs">{value.map((s) => card(s, 'value'))}</div>
        </>
      )}
    </section>
  );
}

/** Start time, then a live countdown inside five minutes (ticks every second only then). */
function RaceClock({ start, isNext }: { start: string; isNext: boolean }) {
  const [now, setNow] = useState(() => Date.now());
  const close = Date.parse(start) - now < 360000;
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), close ? 1000 : 30000);
    return () => clearInterval(id);
  }, [close]);
  const { text, imminent } = raceClock(start, now);
  if (imminent) return <span className="stat soon" role="timer">{text}</span>;
  if (isNext && text !== 'Closed') return <span className="stat next">Next · {text}</span>;
  return <span className="stat up">{text}</span>;
}
